import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { logger } from '../../../../utils/logger';

/**
 * Error Boundary Component
 *
 * Catches React rendering errors and prevents entire app crashes.
 * Displays user-friendly error message with retry option.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ errorInfo });

    // TODO: Send to error tracking service (Sentry, etc.)
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-danger/10 border border-danger rounded">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-danger font-semibold text-sm mb-1">
                Something went wrong
              </h3>
              <p className="text-sm text-text-muted mb-3">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                <details className="text-xs text-text-muted mb-3">
                  <summary className="cursor-pointer hover:text-text-primary">
                    Error details (dev only)
                  </summary>
                  <pre className="mt-2 p-2 bg-background-secondary rounded overflow-x-auto">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
              <button
                onClick={this.handleReset}
                className="text-xs text-accent-primary-base hover:underline"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
